import React from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import SoftBox from "../SoftBox";
import { UseIsAdmin, UseUserDetails } from "../../hooks/auth";
import { toggleViewAsUser } from "../../redux/user/actions";
import { UseCompanyWorkspaces, UseCompanyColors } from "../../hooks/company";
import { setSelectedWorkspace } from "../../redux/workspace/actions";

import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import PsychologyIcon from "@mui/icons-material/Psychology";

/**
 * "See the app as" Player / Admin switcher — a snug segmented control (the
 * track hugs the two options, no dead space) instead of MUI Tabs, whose fixed
 * per-tab minWidth left the frame loose and the active pill misfit.
 */
const AdminViewToggle = ({ light }) => {
  const isAdmin = UseIsAdmin();
  const tabValue = isAdmin ? 1 : 0;
  const dispatch = useDispatch();
  const userDetails = UseUserDetails();
  const navigate = useNavigate();
  // `true` includes sub-workspaces -- without it, a user whose own groupId
  // is a sub-workspace (e.g. Sales > Field Sales) never matches below, so
  // switching to Player view silently kept whatever workspace was being
  // browsed as Admin instead of resetting to the player's own.
  const companyWorkspaces = UseCompanyWorkspaces(true);
  const { primaryColor } = UseCompanyColors();
  const brand = primaryColor || "#1B598E";
  const userWorkspace = companyWorkspaces.find((workspace) => workspace._id === userDetails?.groupId);

  const handleChange = (value) => {
    if (value === tabValue) return;
    dispatch(toggleViewAsUser(!value));
    if (!value && userWorkspace) {
      dispatch(setSelectedWorkspace({ selectedWorkspace: userWorkspace }));
    }
    navigate("/home");
  };

  const route = useLocation().pathname.split("/").slice(1);

  // Extracted from the style object: the repo's eslint forbids nested ternaries.
  const inactiveColor = light ? "rgba(255,255,255,0.85)" : "#475569";
  const segStyle = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 14px",
    borderRadius: "999px",
    border: "none",
    outline: "none",
    cursor: "pointer",
    fontFamily: '"DM Sans", sans-serif',
    fontSize: "0.78rem",
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    transition: "background 150ms ease, color 150ms ease",
    background: active ? brand : "transparent",
    color: active ? "#fff" : inactiveColor,
    boxShadow: active ? "0 1px 3px rgba(16,24,40,0.18)" : "none",
  });

  return (
    <SoftBox mr={2} sx={{ display: route[route.length - 1] === "arena" ? "none" : "block" }}>
      <div
        role="tablist"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "2px",
          padding: "3px",
          borderRadius: "999px",
          background: light ? "rgba(255,255,255,0.16)" : "#eef1f5",
          width: "max-content",
        }}
      >
        <button type="button" role="tab" aria-selected={tabValue === 0} onClick={() => handleChange(0)} style={segStyle(tabValue === 0)}>
          <SportsEsportsIcon sx={{ fontSize: "16px" }} />
          Player
        </button>
        <button type="button" role="tab" aria-selected={tabValue === 1} onClick={() => handleChange(1)} style={segStyle(tabValue === 1)}>
          <PsychologyIcon sx={{ fontSize: "16px" }} />
          Admin
        </button>
      </div>
    </SoftBox>
  );
};

export default AdminViewToggle;
